import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Wallet, Eye, EyeOff, ArrowRightLeft, Gift } from "lucide-react";
import { SubscriptionTopup } from "./SubscriptionTopup";
import { WaveTopupButton } from "@/components/wallet/WaveTopupButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n || 0));
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "—");

// Portefeuille d'abonnement (1 000 FCFA / mois) : solde + statut de visibilité
export const SubscriptionWalletCard = ({ user }) => {
  const [data, setData] = useState(null);
  const isPartner = (user?.roles || [user?.role]).includes("partner");

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/subscription/me`);
      setData(r.data);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const transfer = async () => {
    try {
      await axios.post(`${API}/subscription/transfer-from-sales`, { amount: data.fee });
      toast.success(`${fmt(data.fee)} FCFA transférés depuis vos ventes`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transfert impossible");
    }
  };

  if (!data) return null;
  const active = data.active;

  return (
    <section className={`rounded-[2rem] p-5 md:p-7 border space-y-5 ${active ? "bg-[#0F172A] border-emerald-500/30" : "bg-[#1a0f12] border-red-500/40"}`} data-testid="subscription-wallet-card">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-[#D4AF37] flex items-center gap-2"><Wallet size={14} /> Portefeuille abonnement</p>
          <p className="text-3xl md:text-4xl font-black text-white mt-1" data-testid="subscription-balance">{fmt(data.subscription_balance)} FCFA</p>
          {isPartner && data.sales_balance !== null && (
            <p className="text-[11px] text-slate-400 mt-1" data-testid="subscription-sales-balance">Ventes disponibles : {fmt(data.sales_balance)} FCFA</p>
          )}
        </div>
        <span className={`flex items-center gap-2 px-4 py-2 rounded-full text-[11px] font-black uppercase tracking-widest ${active ? "bg-emerald-500/15 text-emerald-300" : "bg-red-500/15 text-red-300"}`} data-testid="subscription-status">
          {active ? <Eye size={14} /> : <EyeOff size={14} />} {active ? "Visible · actif" : "Suspendu"}
        </span>
      </div>

      <p className="text-sm text-slate-300 leading-relaxed" data-testid="subscription-status-text">
        {data.activation_pending
          ? <>Payez <b>{fmt(data.activation_fee ?? data.fee)} FCFA</b> pour activer votre compte : vous recevez <b>{data.trial_months ?? 2} mois offerts</b>, puis {fmt(data.fee)} FCFA par mois, rechargeables par Wave.</>
          : active
            ? <>{data.trial && <Gift size={14} className="inline mr-1 text-[#D4AF37]" />}Vos produits, services et publications sont visibles jusqu'au <b>{fmtDate(data.paid_until)}</b>. Prochain prélèvement : {fmt(data.fee)} FCFA.</>
            : <>Solde insuffisant : vos produits, services et publications sont <b>masqués aux clients</b>. Rechargez au moins {fmt(data.fee)} FCFA pour être visible immédiatement.</>}
      </p>

      <div className="flex flex-wrap gap-3 items-start">
        <WaveTopupButton user={user} defaultWallet="subscription" onDone={load} testId="subscription-wave-btn" />
        <SubscriptionTopup user={user} onDone={load} />
        {isPartner && data.sales_balance >= data.fee && (
          <button type="button" onClick={transfer} className="rounded-full bg-white/10 hover:bg-white/20 px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-white flex items-center gap-2 transition-colors" data-testid="subscription-transfer-btn">
            <ArrowRightLeft size={14} /> Payer avec mes ventes
          </button>
        )}
      </div>

      {data.history?.length > 0 && (
        <ul className="text-[11px] text-slate-400 space-y-1 border-t border-white/10 pt-3 max-h-32 overflow-y-auto" data-testid="subscription-history">
          {data.history.slice(0, 6).map((h) => (
            <li key={h.id} className="flex justify-between gap-3">
              <span className="truncate">{h.description || h.type} {h.status !== "completed" ? "· en attente" : ""}</span>
              <span className={h.amount < 0 ? "text-red-300" : "text-emerald-300"}>{h.amount > 0 ? "+" : ""}{fmt(h.amount)} F</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
